/*
 * --------------------------------------------------------------------
 * File: \src\utils\useWebSocket.js
 * Created: Monday, 7th October 2025
 * Author: System Consultancy Services Sdn. Bhd.
 * 
 * WebSocket Hooks - Custom hooks for easy WebSocket usage in components
 * --------------------------------------------------------------------
 */

import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWebSocketContext } from './WebSocketContext';
import {
  setConnecting,
  setDisconnected,
} from '../app/websocketSlice';

/**
 * Main hook to use WebSocket in any component
 * @param {Object} options - Hook options
 * @param {Function} options.onMessage - Message handler function
 * @param {Function} options.onOpen - Connection open handler
 * @param {Function} options.onClose - Connection close handler
 * @param {Function} options.onError - Error handler
 * @returns {Object} WebSocket methods and state
 */
export const useWebSocket = (options = {}) => {
  const { onMessage, onOpen, onClose, onError } = options;
  const websocketService = useWebSocketContext();
  const dispatch = useDispatch();
  
  const websocketState = useSelector((state) => state.websocket);
  
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
  }, [onMessage, onOpen, onClose, onError]);

  useEffect(() => {
    // Subscribe to events
    const unsubscribeMessage = websocketService.onMessage((data) => {
      if (onMessageRef.current) {
        onMessageRef.current(data);
      }
    });

    const unsubscribeOpen = websocketService.onOpen((event) => {
      if (onOpenRef.current) {
        onOpenRef.current(event);
      }
    });

    const unsubscribeClose = websocketService.onClose((event) => {
      if (onCloseRef.current) {
        onCloseRef.current(event);
      }
    });

    const unsubscribeError = websocketService.onError((error) => {
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    });

    return () => {
      unsubscribeMessage();
      unsubscribeOpen();
      unsubscribeClose();
      unsubscribeError();
    };
  }, [websocketService]);

  const connect = useCallback((url, connectOptions = {}) => {
    dispatch(setConnecting());
    websocketService.connect(url, connectOptions);
  }, [websocketService, dispatch]);

  const disconnect = useCallback((code, reason) => {
    websocketService.disconnect(code, reason);
    dispatch(setDisconnected());
  }, [websocketService, dispatch]);

  const sendMessage = useCallback((data) => {
    return websocketService.send(data);
  }, [websocketService]);

  return {
    // Methods
    connect,
    disconnect,
    send: sendMessage,
    
    // State
    isConnected: websocketState.isConnected,
    isConnecting: websocketState.isConnecting,
    error: websocketState.error,
    messages: websocketState.messages,
    lastMessage: websocketState.lastMessage,
    reconnectAttempts: websocketState.reconnectAttempts,
    
    // Service instance (for advanced usage)
    service: websocketService,
  };
};

/**
 * Hook to get WebSocket connection status
 * @returns {Object} Connection status
 */
export const useWebSocketStatus = () => {
  const { isConnected, isConnecting, error, reconnectAttempts } = useSelector(
    (state) => state.websocket
  );

  return {
    isConnected,
    isConnecting,
    error,
    reconnectAttempts,
  };
};

/**
 * Hook to get WebSocket messages
 * @param {Function} filter - Optional filter function to filter messages
 * @returns {Array} Messages array
 */
export const useWebSocketMessages = (filter = null) => {
  const { messages, lastMessage } = useSelector((state) => state.websocket);

  const filteredMessages = filter ? messages.filter(filter) : messages;

  return {
    messages: filteredMessages,
    lastMessage,
  };
};

/**
 * Hook to send WebSocket messages
 * @returns {Function} Send function
 */
export const useWebSocketSend = () => {
  const websocketService = useWebSocketContext();

  return useCallback((data) => {
    return websocketService.send(data);
  }, [websocketService]);
};
